import React, { useState, useEffect } from 'react';
import Popup from '../common/popup/Popup';
import { Button, Form, Spinner } from 'react-bootstrap';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';

const CONSENT_OPTIONS = {
  internal_marketing: {
    label: 'Internal Marketing',
    description:
      'We’d like to send you emails about our product updates, special promotions, and company news. You can withdraw your consent at any time from your account settings.',
  },
  third_party_marketing: {
    label: 'Third-Party Marketing',
    description:
      'We’d like to share your contact information with our carefully selected partners for their own promotional offers. You can withdraw your consent at any time from your account settings.',
  },
};

const ConsentPopup = ({ show, onClose }) => {
  const { token } = useAuth();
  const apiClient = useApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [consents, setConsents] = useState({
    internal_marketing: 'opt_out',
    third_party_marketing: 'opt_out',
  });

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    apiClient
      .get('/consent', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const updated = { ...consents };
        data.forEach(item => {
          updated[item.consent_type] = item.consent_action;
        });
        setConsents(updated);
      })
      .catch(() =>
        toast.error('Could not load your consent settings.', {
          position: 'top-center',
          autoClose: 3000,
        })
      )
      .finally(() => setLoading(false));
  }, [show, apiClient, token]);

  if (!show) return null;

  const handleChange = (type, action) => {
    setConsents(prev => ({ ...prev, [type]: action }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(consents).map(([type, action]) =>
          apiClient.post(
            '/consent',
            { consent_type: type, consent_action: action },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success('Consent settings saved.', {
        position: 'top-center',
        autoClose: 3000,
      });
      onClose();
    } catch {
      toast.error('Failed to save consent settings.', {
        position: 'top-center',
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popup
      title="Manage Marketing Consent"
      onCancel={onClose}
      footer={
        <>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>{' '}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </>
      }
      disableOverlayClose={saving}
      disableEscapeClose={saving}
    >
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <Form>
          {Object.entries(CONSENT_OPTIONS).map(([type, { label, description }]) => (
            <Form.Group className="mb-4" key={type}>
              <Form.Label>{label}</Form.Label>
              <Form.Text className="text-muted">{description}</Form.Text>
              <div className="mt-2">
                <Form.Check
                  inline
                  type="radio"
                  label="Opt In"
                  name={type}
                  id={`${type}-opt-in`}
                  checked={consents[type] === 'opt_in'}
                  onChange={() => handleChange(type, 'opt_in')}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Opt Out"
                  name={type}
                  id={`${type}-opt-out`}
                  checked={consents[type] === 'opt_out'}
                  onChange={() => handleChange(type, 'opt_out')}
                />
              </div>
            </Form.Group>
          ))}
        </Form>
      )}
    </Popup>
  );
};

export default ConsentPopup;
